import type { Catalogs, Translation } from "@amtana/i18n";

// The words shared components say on their own: a table's pager, a menu's
// open and close, the language switcher's name, a moment's "3 min ago". One
// key per string, in English and Spanish, read through `useDesignSystemCopy`
// (use-design-system-copy.ts) or, outside React, `translatorFor`.
// A component whose words depend on the screen takes them as a prop instead;
// where it takes one, that prop wins over the line here.

export type DesignSystemCopyKey = keyof typeof EN;
export type DesignSystemCatalog = typeof EN;

const EN = {
  language: "Language",
  close: "Close",
  done: "Done",
  select: "Select…",
  premium: "Premium",
  unread: "Unread",
  dismiss: "Dismiss",
  notifications: "Notifications",
  bellUnread: "{label}, {count} unread",
  copyField: "Copy {label}",
  copiedField: "{label} copied",
  playTitle: "Play {title}",
  unlimited: "Unlimited",
  usedLabel: "{label} used",

  theme: "Theme",
  themeLight: "Light",
  themeSystem: "System",
  themeDark: "Dark",
  switchToDark: "Switch to dark mode",
  switchToLight: "Switch to light mode",

  selectAllRows: "Select all rows",
  selectRow: "Select row",
  showingRange: "Showing {from}–{to} of {total}",
  previous: "Previous",
  next: "Next",
  pageStatus: { one: "Page {page} · 1 row", other: "Page {page} · {count} rows" },
  rowsPerPage: "Rows per page",
  sortBy: "Sort by {column}",
  filterSelected: "{count} selected",
  clearFilter: "Clear filter",
  dateFrom: "From {date}",
  dateUntil: "Until {date}",
  rangeFrom: "From",
  rangeTo: "To",

  quickSearch: "Quick Search",
  quickSearchWithShortcut: "Quick Search ({shortcut})",
  mainNavigation: "Main",
  mobileNavigation: "Mobile",
  closeMenu: "Close menu",
  openMenu: "Open menu",
  expandSidebar: "Expand sidebar",
  collapseSidebar: "Collapse sidebar",
  sections: "Sections",
  breadcrumb: "Breadcrumb",

  commandPalette: "Command palette",
  escapeKey: "esc",
  commands: "Commands",
  nothingMatches: "Nothing matches “{query}”.",
  toMove: "to move",
  toOpen: "to open",

  providerGmail: "Gmail",
  providerOutlook: "Outlook",
  providerSendgrid: "Sendgrid",
  providerSes: "Amazon SES",
  providerSmtp: "SMTP",

  justNow: "just now",
  relativeAgo: "{amount} {unit} ago",
  relativeAhead: "in {amount} {unit}",

  tunerLabel: "Shader tuning",
  tunerTitle: "Shader · Warp ({target})",
  tunerAiButtonName: "ai-button",
  tunerReset: "Reset",
  tunerCopied: "Copied",
  tunerCopyLink: "Copy Paper link",
  tunerWarp: "Warp",
  tunerBackground: "Background",
  tunerAiButton: "AI button",
  tunerOverlays: "Overlays",
  tunerPalettes: "Palettes",
  tunerShape: "shape",
} as const;

const ES: Translation<DesignSystemCatalog> = {
  language: "Idioma",
  close: "Cerrar",
  done: "Hecho",
  select: "Seleccionar…",
  premium: "Premium",
  unread: "Sin leer",
  dismiss: "Descartar",
  notifications: "Notificaciones",
  bellUnread: "{label}, {count} sin leer",
  copyField: "Copiar {label}",
  copiedField: "Copiado: {label}",
  playTitle: "Reproducir {title}",
  unlimited: "Ilimitado",
  usedLabel: "Uso de {label}",

  theme: "Tema",
  themeLight: "Claro",
  themeSystem: "Sistema",
  themeDark: "Oscuro",
  switchToDark: "Cambiar a modo oscuro",
  switchToLight: "Cambiar a modo claro",

  selectAllRows: "Seleccionar todas las filas",
  selectRow: "Seleccionar fila",
  showingRange: "Mostrando {from}–{to} de {total}",
  previous: "Anterior",
  next: "Siguiente",
  pageStatus: { one: "Página {page} · 1 fila", other: "Página {page} · {count} filas" },
  rowsPerPage: "Filas por página",
  sortBy: "Ordenar por {column}",
  filterSelected: "{count} elegidos",
  clearFilter: "Quitar filtro",
  dateFrom: "Desde el {date}",
  dateUntil: "Hasta el {date}",
  rangeFrom: "Desde",
  rangeTo: "Hasta",

  quickSearch: "Búsqueda rápida",
  quickSearchWithShortcut: "Búsqueda rápida ({shortcut})",
  mainNavigation: "Principal",
  mobileNavigation: "Móvil",
  closeMenu: "Cerrar menú",
  openMenu: "Abrir menú",
  expandSidebar: "Expandir barra lateral",
  collapseSidebar: "Contraer barra lateral",
  sections: "Secciones",
  breadcrumb: "Ruta de navegación",

  commandPalette: "Paleta de comandos",
  escapeKey: "esc",
  commands: "Comandos",
  nothingMatches: "Nada coincide con “{query}”.",
  toMove: "para mover",
  toOpen: "para abrir",

  providerGmail: "Gmail",
  providerOutlook: "Outlook",
  providerSendgrid: "Sendgrid",
  providerSes: "Amazon SES",
  providerSmtp: "SMTP",

  justNow: "justo ahora",
  relativeAgo: "hace {amount} {unit}",
  relativeAhead: "en {amount} {unit}",

  tunerLabel: "Ajuste del shader",
  tunerTitle: "Shader · Warp ({target})",
  tunerAiButtonName: "ai-button",
  tunerReset: "Restablecer",
  tunerCopied: "Copiado",
  tunerCopyLink: "Copiar enlace de Paper",
  tunerWarp: "Warp",
  tunerBackground: "Fondo",
  tunerAiButton: "Botón de IA",
  tunerOverlays: "Superposiciones",
  tunerPalettes: "Paletas",
  tunerShape: "shape",
};

export const DESIGN_SYSTEM_CATALOGS: Catalogs<DesignSystemCatalog> = { en: EN, es: ES };
